import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, LogOut, Plus, Trash2, Save, Upload, ArrowLeft } from "lucide-react";
import { PAGES } from "../config/pages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mc360-admin-token";

const TEXT_FIELDS = [
  { key: "seoTitle", label: "SEO Title" },
  { key: "metaDescription", label: "Meta Description", textarea: true },
  { key: "projectType", label: "Project Type" },
  { key: "kicker", label: "Hero Kicker" },
  { key: "intro", label: "Intro Paragraph", textarea: true },
  { key: "heroImage", label: "Hero Image URL" },
  { key: "heroImageAlt", label: "Hero Image Alt Text" },
  { key: "visualImage", label: "Visual Section Image URL" },
  { key: "visualImageAlt", label: "Visual Section Alt Text" },
  { key: "guideTitle", label: "Field Guide Title" },
  { key: "cardBlurb", label: "Card Blurb" },
  { key: "ctaText", label: "CTA Button Text" },
];

const JSON_FIELDS = [
  { key: "h1Lines", label: "H1 Lines" },
  { key: "primaryCta", label: "Primary CTA" },
  { key: "calculator", label: "Calculator Preview" },
  { key: "chapters", label: "Guide Chapters" },
  { key: "faqs", label: "FAQs" },
  { key: "relatedSlugs", label: "Related Slugs" },
  { key: "visualStat", label: "Visual Stat" },
];

const BLANK_PAGE = {
  slug: "",
  projectType: "Tools & Converters",
  seoTitle: "",
  metaDescription: "",
  kicker: "MC360 // Tools & Converters",
  h1Lines: [[{ t: "NEW PAGE", accent: true }]],
  intro: "",
  heroImage: "",
  heroImageAlt: "",
  primaryCta: { label: "Try the Calculator", href: "https://matcalc360.pro" },
  secondaryCta: { label: "See how it works", href: "#how-it-works" },
  calculator: { name: "", path: "/", inputs: [], outputs: [] },
  guideTitle: "",
  chapters: [],
  visualImage: "",
  visualImageAlt: "",
  visualStat: { kicker: "", value: "", label: "" },
  faqs: [],
  relatedSlugs: [],
  cardBlurb: "",
  ctaText: "Try the Calculator",
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pages, setPages] = useState({});
  const [editing, setEditing] = useState(null);
  const [jsonDrafts, setJsonDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEditing(null);
  };

  const loadPages = async () => {
    try {
      const res = await fetch(`${API}/pages`);
      if (res.ok) setPages(await res.json());
    } catch (e) {}
  };

  useEffect(() => {
    if (token) loadPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (page) => {
    setEditing({ ...BLANK_PAGE, ...page });
    const drafts = {};
    JSON_FIELDS.forEach(({ key }) => {
      drafts[key] = JSON.stringify(page[key] ?? BLANK_PAGE[key], null, 2);
    });
    setJsonDrafts(drafts);
    setError("");
    setNotice("");
  };

  const save = async () => {
    setError("");
    setNotice("");
    const page = { ...editing };
    for (const { key, label } of JSON_FIELDS) {
      try {
        page[key] = JSON.parse(jsonDrafts[key]);
      } catch (err) {
        setError(`${label} is not valid JSON`);
        return;
      }
    }
    const slug = (page.slug || "").trim();
    if (!slug) {
      setError("A URL slug is required (e.g. mulch-calculator)");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/pages/${slug}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify(page),
      });
      if (res.status === 401) {
        logout();
        return;
      }
      if (!res.ok) throw new Error("Save failed");
      setNotice(`Saved. Live now at /${slug}`);
      await loadPages();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    await fetch(`${API}/admin/pages/${slug}`, { method: "DELETE", headers: authHeaders });
    await loadPages();
  };

  const seedFromConfig = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/seed`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(PAGES),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Import failed");
      setNotice(`Imported ${data.seeded} pages from the config file into the database`);
      await loadPages();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const pageList = Object.values(pages);

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-bg px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm border border-ink-line bg-ink-surface p-8">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center bg-accent">
              <Calculator className="h-4.5 w-4.5 text-[#0B0B0B]" strokeWidth={2.5} />
            </span>
            <span className="font-display text-lg uppercase tracking-tight text-ink-text">
              MatCalc<span className="text-accent">360</span>
            </span>
          </div>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-ink-muted">Admin // Landing Pages</p>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            data-testid="admin-login-email-input"
            className="mt-6 w-full border border-ink-line bg-ink-bg px-4 py-3 text-sm text-ink-text placeholder:text-ink-muted focus:border-accent focus:outline-none"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="admin-login-password-input"
            className="mt-3 w-full border border-ink-line bg-ink-bg px-4 py-3 text-sm text-ink-text placeholder:text-ink-muted focus:border-accent focus:outline-none"
          />
          {error && (
            <p className="mt-3 font-mono text-xs text-red-400" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit-button"
            className="mt-6 w-full bg-accent px-4 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-accent-dark disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <Link to="/" data-testid="admin-login-back-link" className="mt-4 block font-mono text-xs uppercase tracking-[0.2em] text-ink-muted hover:text-accent">
            ← Back to site
          </Link>
        </form>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="min-h-screen bg-ink-bg" data-testid="admin-editor">
        <div className="mx-auto max-w-4xl px-6 py-10">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setEditing(null)}
              data-testid="admin-back-button"
              className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink-muted hover:text-accent"
            >
              <ArrowLeft className="h-4 w-4" /> All pages
            </button>
            <button
              onClick={save}
              disabled={busy}
              data-testid="admin-save-button"
              className="flex items-center gap-2 bg-accent px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white hover:bg-accent-dark disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save page"}
            </button>
          </div>

          <h1 className="mt-6 font-display text-2xl uppercase tracking-tight text-ink-text">
            {editing.slug ? `Edit: ${editing.slug}` : "New landing page"}
          </h1>
          {error && <p className="mt-3 font-mono text-xs text-red-400" data-testid="admin-error">{error}</p>}
          {notice && <p className="mt-3 font-mono text-xs text-green-400" data-testid="admin-notice">{notice}</p>}

          <div className="mt-8 space-y-5">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">URL Slug</label>
              <input
                value={editing.slug}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                placeholder="mulch-calculator"
                data-testid="admin-field-slug"
                className="mt-1.5 w-full border border-ink-line bg-ink-surface px-4 py-3 font-mono text-sm text-ink-text focus:border-accent focus:outline-none"
              />
            </div>
            {TEXT_FIELDS.map(({ key, label, textarea }) => (
              <div key={key}>
                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">{label}</label>
                {textarea ? (
                  <textarea
                    rows={3}
                    value={editing[key] || ""}
                    onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
                    data-testid={`admin-field-${key}`}
                    className="mt-1.5 w-full border border-ink-line bg-ink-surface px-4 py-3 text-sm text-ink-text focus:border-accent focus:outline-none"
                  />
                ) : (
                  <input
                    value={editing[key] || ""}
                    onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
                    data-testid={`admin-field-${key}`}
                    className="mt-1.5 w-full border border-ink-line bg-ink-surface px-4 py-3 text-sm text-ink-text focus:border-accent focus:outline-none"
                  />
                )}
              </div>
            ))}
            {JSON_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">{label} (JSON)</label>
                <textarea
                  rows={key === "faqs" || key === "chapters" ? 10 : 5}
                  value={jsonDrafts[key]}
                  onChange={(e) => setJsonDrafts({ ...jsonDrafts, [key]: e.target.value })}
                  data-testid={`admin-json-${key}`}
                  className="mt-1.5 w-full border border-ink-line bg-ink-surface px-4 py-3 font-mono text-xs text-ink-text focus:border-accent focus:outline-none"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-bg" data-testid="admin-dashboard">
      <div className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center bg-accent">
              <Calculator className="h-4.5 w-4.5 text-[#0B0B0B]" strokeWidth={2.5} />
            </span>
            <div>
              <span className="font-display text-lg uppercase tracking-tight text-ink-text">
                MatCalc<span className="text-accent">360</span>
              </span>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">Landing Page Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => startEdit(BLANK_PAGE)}
              data-testid="admin-new-page-button"
              className="flex items-center gap-2 bg-accent px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white hover:bg-accent-dark"
            >
              <Plus className="h-4 w-4" /> New page
            </button>
            <button
              onClick={seedFromConfig}
              disabled={busy}
              data-testid="admin-seed-button"
              className="flex items-center gap-2 border border-ink-line px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-ink-text hover:border-accent hover:text-accent disabled:opacity-50"
            >
              <Upload className="h-4 w-4" /> Import config file
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              aria-label="Log out"
              className="flex h-10 w-10 items-center justify-center border border-ink-line text-ink-text hover:border-accent hover:text-accent"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        {notice && <p className="mt-6 font-mono text-xs text-green-400" data-testid="admin-notice">{notice}</p>}
        {error && <p className="mt-6 font-mono text-xs text-red-400" data-testid="admin-error">{error}</p>}

        {pageList.length === 0 ? (
          <div className="mt-16 border border-ink-line bg-ink-surface p-10 text-center" data-testid="admin-empty-state">
            <p className="font-mono text-sm uppercase tracking-[0.2em] text-ink-muted">Database is empty</p>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink-muted">
              The site is currently running on the built-in config file. Click "Import config file" to copy all pages into the database — from then on, edits here go live instantly without a redeploy.
            </p>
          </div>
        ) : (
          <div className="mt-10 border border-ink-line" data-testid="admin-pages-table">
            {pageList.map((page) => (
              <div
                key={page.slug}
                data-testid={`admin-page-row-${page.slug}`}
                className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-line bg-ink-surface px-5 py-4 last:border-b-0"
              >
                <div className="min-w-0">
                  <p className="font-mono text-sm font-bold text-ink-text">/{page.slug}</p>
                  <p className="mt-0.5 truncate text-xs text-ink-muted">{page.seoTitle}</p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`/${page.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-view-${page.slug}`}
                    className="border border-ink-line px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-ink-muted hover:border-accent hover:text-accent"
                  >
                    View
                  </a>
                  <button
                    onClick={() => startEdit(page)}
                    data-testid={`admin-edit-${page.slug}`}
                    className="border border-ink-line px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-ink-text hover:border-accent hover:text-accent"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(page.slug)}
                    data-testid={`admin-delete-${page.slug}`}
                    aria-label={`Delete ${page.slug}`}
                    className="flex h-8 w-8 items-center justify-center border border-ink-line text-ink-muted hover:border-red-400 hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
          {pageList.length} pages in database · edits go live instantly · config file remains the fallback
        </p>
      </div>
    </div>
  );
}
